import React, { useState, useEffect, useMemo } from 'react';
import {
  Container, Typography, Card, CardContent, List, ListItem,
  ListItemText, Button, CircularProgress, Alert, Chip,
  Box, TextField, InputAdornment
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import GroupRemoveIcon from '@mui/icons-material/GroupRemove';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import ContextNavBar from '../../components/ContextNavBar';
import { membrosSemAcompanhamento, lideresSemDiscipulador } from '../../api/jornada';

const LIME = '#00F1A7';
const DARK = '#1C1C1E';

const LETRAS = '#ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

export default function AlertasGestao() {
  const navigate = useNavigate();
  const [semAcomp, setSemAcomp] = useState([]);
  const [lideres, setLideres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [filtro, setFiltro] = useState('');
  const [inicial, setInicial] = useState('');

  const filtrar = (lista) => lista.filter(m => {
    if (inicial && (!m.nome || m.nome[0].toUpperCase() !== inicial)) return false;
    if (filtro && !m.nome?.toLowerCase().includes(filtro.toLowerCase())) return false;
    return true;
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const semAcompFiltrados = useMemo(() => filtrar(semAcomp), [semAcomp, filtro, inicial]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const lideresFiltrados = useMemo(() => filtrar(lideres), [lideres, filtro, inicial]);

  const fetch = () => {
    setLoading(true);
    setErro('');
    Promise.all([membrosSemAcompanhamento(), lideresSemDiscipulador()])
      .then(([semAcompData, lideresData]) => {
        setSemAcomp(semAcompData);
        setLideres(lideresData);
      })
      .catch(() => setErro('Nao foi possivel carregar os alertas.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetch(); }, []);

  if (loading) {
    return (
      <>
        <Header />
        <ContextNavBar context="jornada" />
        <Container maxWidth="md" sx={{ py: { xs: 2, md: 4 }, px: { xs: 1, md: 2 }, textAlign: 'center' }}>
          <CircularProgress />
        </Container>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <ContextNavBar context="jornada" />
      <Container maxWidth="md" sx={{ py: { xs: 2, md: 4 }, px: { xs: 1, md: 2 } }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, display: 'flex', alignItems: 'center', gap: 1, fontSize: { xs: '1.2rem', md: '1.5rem' } }}>
          <WarningAmberIcon sx={{ color: LIME }} /> Alertas de Gestao
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            placeholder="Buscar por nome..."
            value={filtro}
            onChange={e => setFiltro(e.target.value)}
            size="small"
            sx={{ minWidth: 260 }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
            }}
          />
          <Box sx={{ display: 'flex', gap: 0.3, flexWrap: 'wrap' }}>
            <Chip label="Todas" size="small" variant={inicial === '' ? 'filled' : 'outlined'}
              onClick={() => setInicial('')} sx={{ cursor: 'pointer', fontWeight: 600 }} />
            {LETRAS.map(l => (
              <Chip key={l} label={l} size="small" variant={inicial === l ? 'filled' : 'outlined'}
                color={inicial === l ? 'primary' : 'default'}
                onClick={() => setInicial(l === inicial ? '' : l)}
                sx={{ cursor: 'pointer', minWidth: 32, fontWeight: 600 }} />
            ))}
          </Box>
        </Box>

        {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}

        {/* Membros sem acompanhamento */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonRemoveIcon sx={{ color: '#f44336' }} /> Membros sem acompanhamento
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Membros sem discipulador e sem grupo de crescimento.
            </Typography>
            {semAcomp.length === 0 ? (
              <Alert severity="success" sx={{ bgcolor: '#e8f5e9' }}>
                Todos os membros possuem acompanhamento!
              </Alert>
              ) : (
              <List disablePadding>
                {semAcompFiltrados.length === 0 && filtro && (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>Nenhum membro encontrado com esse filtro.</Typography>
                )}
                {semAcompFiltrados.map((m) => (
                  <ListItem
                    key={m.id}
                    sx={{ px: 0, borderBottom: '1px solid #eee' }}
                    secondaryAction={
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => navigate(`/discipulados/novo?membro_id=${m.id}`)}
                        sx={{ borderColor: LIME, color: DARK, '&:hover': { bgcolor: LIME, color: DARK } }}
                      >
                        Vincular Discipulado
                      </Button>
                    }
                  >
                    <ListItemText
                      primary={m.nome}
                      secondary={m.email || m.telefone || ''}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </CardContent>
        </Card>

        {/* Lideres de GC sem discipulador */}
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <GroupRemoveIcon sx={{ color: '#ff9800' }} /> Lideres de GC sem discipulador
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Lideres de grupo de crescimento que nao possuem discipulador.
            </Typography>
            {lideres.length === 0 ? (
              <Alert severity="success" sx={{ bgcolor: '#e8f5e9' }}>
                Todos os lideres de GC possuem discipulador!
              </Alert>
              ) : (
              <List disablePadding>
                {lideresFiltrados.length === 0 && filtro && (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>Nenhum líder encontrado com esse filtro.</Typography>
                )}
                {lideresFiltrados.map((l) => (
                  <ListItem
                    key={l.gc_id}
                    sx={{ px: 0, borderBottom: '1px solid #eee' }}
                    secondaryAction={
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => navigate(`/discipulados/novo?membro_id=${l.lider_id}`)}
                        sx={{ borderColor: LIME, color: DARK, '&:hover': { bgcolor: LIME, color: DARK } }}
                      >
                        Atribuir Discipulador
                      </Button>
                    }
                  >
                    <ListItemText
                      primary={l.lider_nome}
                      secondary={
                        <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                          {l.gc_nome && <Chip label={l.gc_nome} size="small" sx={{ bgcolor: '#e0e0e0' }} />}
                          {l.funcao && <Chip label={l.funcao} size="small" variant="outlined" />}
                        </Box>
                      }
                      secondaryTypographyProps={{ component: 'div' }}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </CardContent>
        </Card>
      </Container>
      <Footer />
    </>
  );
}
